import { Controller } from "@hotwired/stimulus"

// The sub's full PCO page — the GC Finances PCO page pointed at the sub's
// request (Phelipe 2026-08-21): every field and priced line saves
// automatically a beat after typing stops, files upload straight from the
// Supporting documents dropzone and register on the next save, and a draft
// can be binned behind a styled confirm. Grand total = lines + markup.
export default class extends Controller {
  static targets = ["lines", "lineTemplate", "total", "linesTotal", "chips", "fileInput",
                    "saveStatus", "deleteModal"]
  static values = { presignUrl: String, updateUrl: String, editable: Boolean }

  connect() {
    this._pendingFiles = []
    this.recalc()
    // Fresh PCO: put the cursor in the first line's Description so typing
    // starts where the work is described, not in the item number — same as
    // the GC PCO page (Phelipe 2026-08-21). Skipped once the line has text,
    // so reopening a part-filled draft doesn't yank focus.
    if (this.editableValue && this.hasLinesTarget) {
      const first = this.linesTarget.querySelector('[data-scp-f="description"]')
      if (first && !first.value) first.focus()
    }
  }

  addLine(e) {
    e?.preventDefault()
    // GC-grid behaviors (Phelipe 2026-08-21): new rows auto-number and
    // typing starts in Description, not Item — same as the GC PCO page.
    const next = this.linesTarget.querySelectorAll("[data-scp-line]").length + 1
    this.linesTarget.appendChild(this.lineTemplateTarget.content.cloneNode(true))
    const rows = this.linesTarget.querySelectorAll("[data-scp-line]")
    const row = rows[rows.length - 1]
    const item = row?.querySelector('[data-scp-f="item"]')
    if (item && !item.value) item.value = String(next)
    row?.querySelector('[data-scp-f="description"]')?.focus()
  }

  removeLine(e) {
    e.currentTarget.closest("[data-scp-line]")?.remove()
    this.recalc()
    this._queueSave()
  }

  fieldChanged() { this.recalc(); this._queueSave() }
  lineInput() { this.recalc(); this._queueSave() }

  moneyBlur(e) {
    const v = this._num(e.currentTarget.value)
    e.currentTarget.value = v ? this._money(v) : ""
    this.recalc()
  }

  recalc() {
    let sum = 0
    this.linesTarget.querySelectorAll("[data-scp-line]").forEach((row) => {
      const qty = this._num(row.querySelector('[data-scp-f="quantity"]')?.value)
      const price = this._num(row.querySelector('[data-scp-f="unit_price"]')?.value)
      const total = qty * price
      sum += total
      const cell = row.querySelector("[data-scp-line-total]")
      if (cell) cell.textContent = `$${this._money(total)}`
    })
    if (this.hasLinesTotalTarget) this.linesTotalTarget.textContent = `$${this._money(sum)}`
    const markup = this._num(this.element.querySelector('[data-scp-field="markup_pct"]')?.value)
    const grand = sum + (sum * markup) / 100
    if (this.hasTotalTarget) this.totalTarget.textContent = `$${this._money(grand)}`
  }

  // ── Autosave — GC PCO parity: a beat after the last keystroke. ─────────
  _queueSave() {
    if (!this.editableValue) return
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saving…"
    clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this._save(), 800)
  }

  async _save() {
    const field = (name) => this.element.querySelector(`[data-scp-field="${name}"]`)?.value ?? ""
    // Snapshot — files that finish uploading while this save is in flight
    // stay queued for the next one instead of being dropped.
    const sent = this._pendingFiles.slice()
    const body = {
      title: field("title"),
      description: field("description"),
      time_extension_days: field("time_extension_days"),
      change_lines: Array.from(this.linesTarget.querySelectorAll("[data-scp-line]")).map((row) => {
        const f = (k) => row.querySelector(`[data-scp-f="${k}"]`)?.value ?? ""
        return { item: f("item"), description: f("description"), quantity: f("quantity"),
                 unit: f("unit"), unit_price: f("unit_price") }
      }),
      cache_names: sent.map((p) => p.cache_name),
      filenames: sent.map((p) => p.filename)
    }
    try {
      const resp = await fetch(this.updateUrlValue, {
        method: "POST", headers: this._headers(), body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't save")
      // These files are registered now — don't send them again next save.
      this._pendingFiles = this._pendingFiles.filter((p) => !sent.includes(p))
      sent.forEach((p) => p.chip?.querySelector("[data-scp-pending]")?.remove())
      if (this.hasSaveStatusTarget) {
        this.saveStatusTarget.textContent = "Saved ✓"
        clearTimeout(this._statusTimer)
        this._statusTimer = setTimeout(() => { this.saveStatusTarget.textContent = "" }, 2000)
      }
    } catch (err) {
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = err.message
    }
  }

  // ── Supporting documents — dropzone + click to browse. ────────────────
  pickFiles() { this.fileInputTarget.click() }

  dragOver(e) { e.preventDefault(); e.currentTarget.classList.add("border-brand", "bg-brand/5") }
  dragLeave(e) { e.currentTarget.classList.remove("border-brand", "bg-brand/5") }
  dropFiles(e) {
    e.preventDefault()
    this.dragLeave(e)
    Array.from(e.dataTransfer?.files || []).forEach((file) => this._upload(file))
  }

  filesChosen() {
    Array.from(this.fileInputTarget.files || []).forEach((file) => this._upload(file))
    this.fileInputTarget.value = ""
  }

  async _upload(file) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 rounded-lg px-2.5 py-1 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span class="max-w-[180px] truncate">${this._esc(file.name)}</span>`
    this.chipsTarget.appendChild(chip)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const data = await presign.json()
      if (!presign.ok || !data.uploads?.[0]) throw new Error(data.error || "Upload failed")
      const u = data.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      chip.innerHTML = `<svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/></svg><span class="max-w-[180px] truncate">${this._esc(file.name)}</span> <span class="text-gray-400" data-scp-pending>saving…</span>`
      this._pendingFiles.push({ cache_name: u.cache_name, filename: file.name, chip })
      this._queueSave()
    } catch (err) {
      chip.remove()
      this._toast(err.message, "alert")
    }
  }

  // ── Delete-draft confirm modal. ────────────────────────────────────────
  openDeleteModal() { if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.remove("hidden") }
  closeDeleteModal() { if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.add("hidden") }
  stopPropagation(e) { e.stopPropagation() }

  _num(s) { return parseFloat(String(s || "").replace(/[$,%\s]/g, "")) || 0 }
  _money(n) { return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
  _esc(s) { return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }
  _headers() {
    return { "Content-Type": "application/json", "Accept": "application/json",
             "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content }
  }
  _toast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.textContent = message
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    document.body.prepend(div)
  }
}
